import * as Sentry from "@sentry/react";
import { SYNC_PUSH_OUTCOME_EVENT } from "@shared/lib/sync-push-outcome";

type SSEEventHandler = (data: unknown) => void;

interface SSEClientOptions {
  url: string;
  onEvent?: SSEEventHandler;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onReconnectExhausted?: () => void;
}

class SSEConnectionManager {
  private eventSource: EventSource | null = null;
  private handlers = new Map<string, Set<SSEEventHandler>>();
  private reconnectAttempts = 0;
  // Attempts to make before warning the user about a sustained loss. With
  // exponential backoff capped at 30s this is ~3 minutes of continuous failure
  // before the user-facing toast fires — long enough that ordinary transient
  // blips (server restart in dev, brief network drops) never surface it.
  private maxReconnectAttempts = 10;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private url: string;
  private connected = false;
  private stopped = false;
  private exhaustedWarned = false;
  private onConnectCallback?: () => void;
  private onDisconnectCallback?: () => void;
  private onReconnectExhaustedCallback?: () => void;
  private onReconnectedCallback?: () => void;

  constructor(options: SSEClientOptions) {
    this.url = options.url;
    if (options.onConnect !== undefined) this.onConnectCallback = options.onConnect;
    if (options.onDisconnect !== undefined) this.onDisconnectCallback = options.onDisconnect;
    if (options.onReconnectExhausted !== undefined) this.onReconnectExhaustedCallback = options.onReconnectExhausted;
    if (options.onEvent) {
      this.on("*", options.onEvent);
    }
  }

  connect() {
    this.stopped = false;
    if (this.eventSource) return;

    try {
      this.eventSource = new EventSource(this.url, { withCredentials: true });

      this.eventSource.onopen = () => {
        this.connected = true;
        this.reconnectAttempts = 0;
        // A successful (re)connect clears any sustained-loss warning that was
        // surfaced to the user; otherwise the persistent toast would linger
        // long after real-time updates resumed.
        if (this.exhaustedWarned) {
          this.exhaustedWarned = false;
          this.onReconnectedCallback?.();
        }
        this.onConnectCallback?.();
      };

      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as { type?: string; eventType?: string; };
          if (data.type === "connected") return;
          if (data.eventType) {
            this.emit(data.eventType, data);
          }
          this.emit("*", data);
        } catch (err) {
          console.error("[sse] Failed to parse SSE message:", err);
          Sentry.captureException(err, { extra: { context: "sse-message-parse", raw: event.data?.slice(0, 200) } });
        }
      };

      this.eventSource.onerror = () => {
        // Close this stream (stopping the browser's native retry) and drive
        // recovery through our own exponential backoff. A transient blip
        // reconnects within an attempt or two and `onopen` resets the counter,
        // so the sustained-loss warning only fires after a genuine, prolonged
        // outage (~maxReconnectAttempts of continuous failure).
        this.disconnect(false);
        this.scheduleReconnect();
      };

      const knownEvents = [
        "payment_received",
        "invoice_created",
        "invoice_approved",
        "invoice_sent",
        "invoice_disputed",
        "invoice_voided",
        "invoice_bulk_import_started",
        "invoice_bulk_import_completed",
        "invoice_bulk_approve_started",
        "invoice_bulk_approve_completed",
        "invoice_bulk_sync_completed",
        "bill_created",
        "bill_approved",
        "bill_overdue",
        "invoice_overdue",
        "customer_created",
        "vendor_created",
        "dispute_opened",
        "dispute_status_changed",
        "dispute_resolved",
        "demo_reply_pending",
        "demo_reply_resolved",
        "journal_entry_posted",
        "budget_line_updated",
        SYNC_PUSH_OUTCOME_EVENT,
      ];
      for (const eventName of knownEvents) {
        this.eventSource.addEventListener(eventName, (event: MessageEvent) => {
          try {
            const data = JSON.parse(event.data);
            this.emit(eventName, data);
            this.emit("*", data);
          } catch (err) {
            console.error("[sse] Failed to parse SSE event:", err);
            Sentry.captureException(err, {
              extra: { context: "sse-event-parse", eventName, raw: event.data?.slice(0, 200) },
            });
          }
        });
      }
    } catch (err) {
      console.error("[sse] Failed to connect to SSE endpoint:", err);
      Sentry.captureException(err, { extra: { context: "sse-connect" } });
      this.scheduleReconnect();
    }
  }

  disconnect(manual = true) {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    if (this.connected) {
      this.connected = false;
      this.onDisconnectCallback?.();
    }
    if (manual) {
      this.stopped = true;
    }
  }

  private scheduleReconnect() {
    if (this.stopped) return;
    // Warn the user exactly once when the loss looks sustained, but keep
    // retrying at the capped backoff interval afterwards so that, if the
    // connection recovers, `onopen` can clear the warning. Never permanently
    // give up — that would leave real-time updates dead until a full reload.
    if (this.reconnectAttempts >= this.maxReconnectAttempts && !this.exhaustedWarned) {
      this.exhaustedWarned = true;
      this.onReconnectExhaustedCallback?.();
    }
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  on(eventName: string, handler: SSEEventHandler) {
    if (!this.handlers.has(eventName)) {
      this.handlers.set(eventName, new Set());
    }
    this.handlers.get(eventName)!.add(handler);
    return () => {
      this.handlers.get(eventName)?.delete(handler);
    };
  }

  off(eventName: string, handler: SSEEventHandler) {
    this.handlers.get(eventName)?.delete(handler);
  }

  private emit(eventName: string, data: unknown) {
    this.handlers.get(eventName)?.forEach((handler) => {
      try {
        handler(data);
      } catch (err) {
        console.error("[sse] SSE event handler threw an error:", err);
        Sentry.captureException(err, { extra: { context: "sse-event-handler", eventName } });
      }
    });
  }

  isConnected() {
    return this.connected;
  }

  setReconnectExhaustedCallback(cb: () => void) {
    this.onReconnectExhaustedCallback = cb;
  }

  setReconnectedCallback(cb: () => void) {
    this.onReconnectedCallback = cb;
  }
}

let instance: SSEConnectionManager | null = null;

export function setSSEReconnectExhaustedHandler(cb: () => void) {
  if (!instance) {
    instance = new SSEConnectionManager({ url: "/api/events/stream" });
  }
  instance.setReconnectExhaustedCallback(cb);
}

export function setSSEReconnectedHandler(cb: () => void) {
  if (!instance) {
    instance = new SSEConnectionManager({ url: "/api/events/stream" });
  }
  instance.setReconnectedCallback(cb);
}

export function getSSEClient(): SSEConnectionManager {
  if (!instance) {
    instance = new SSEConnectionManager({ url: "/api/events/stream" });
  }
  return instance;
}

export function destroySSEClient() {
  if (instance) {
    instance.disconnect();
    instance = null;
  }
}

export { SSEConnectionManager };
